"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'default' | 'destructive';
}

interface ConfirmationResult {
  confirmed: boolean;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<ConfirmationResult>;
}

const ConfirmationContext = createContext<ConfirmationContextType | null>(null);

interface ConfirmationState {
  isOpen: boolean;
  options: ConfirmationOptions | null;
  resolve: ((result: ConfirmationResult) => void) | null;
}

export function ConfirmationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmationState>({
    isOpen: false,
    options: null,
    resolve: null,
  });

  const confirm = useCallback((options: ConfirmationOptions): Promise<ConfirmationResult> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (state.resolve) {
      state.resolve({ confirmed: true });
    }
    setState({ isOpen: false, options: null, resolve: null });
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    if (state.resolve) {
      state.resolve({ confirmed: false });
    }
    setState({ isOpen: false, options: null, resolve: null });
  }, [state.resolve]);

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      {state.isOpen && state.options && (
        <ConfirmationDialog
          options={state.options}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </ConfirmationContext.Provider>
  );
}

interface ConfirmationDialogProps {
  options: ConfirmationOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmationDialog({ options, onConfirm, onCancel }: ConfirmationDialogProps) {
  const {
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "default"
  } = options;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-card border border-border p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
        <h3 className="text-lg font-medium text-foreground mb-2">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          {message}
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-border text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              type === 'destructive'
                ? "bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                : "bg-primary text-primary-foreground hover:bg-primary/80"
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirmation() {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error("useConfirmation must be used within a ConfirmationProvider");
  }
  return context;
}